import type { ItemFieldRow } from './item-field-reader';
import type { FormulaReadDependencies } from './formula-read-context';
import type { FormulaVariableMetadata } from './formula-variable-resolution';
import { createFormulaReadContext } from './formula-read-context';
import { scoreCarrierIntegerLayerArithmetic } from './formula-variable-resolution';
import { layerEntryNeedsResolve, resolveLayerEntry } from './layer-entry-resolver';
import { readOverrideEntryAbilityId } from './layer-cell-readers';
import { appendPhpCompileArray } from './status-row-construction';
import { phpJsonString } from './php-json-values';
import { phpAdd, phpFloatToInteger } from './php-integer-arithmetic';
import type { PhpArithmetic } from './php-integer-arithmetic';

type Row = Record<string, unknown>;
const arrayLike = (v: unknown): v is ItemFieldRow => v !== null && typeof v === 'object';
const has = (v: unknown, key: string): boolean => arrayLike(v) && Object.prototype.hasOwnProperty.call(v, key);
const own = (v: unknown, key: string): unknown => has(v, key) ? (v as Row)[key] : undefined;
const path = (v: unknown, ...keys: string[]): unknown => keys.reduce(own, v);
const valid = (v: string): boolean => /^[0-9a-f]{8}(?:-[0-9a-f]{4}){3}-[0-9a-f]{12}(?:\n)?(?![\s\S])/i.test(v);
const lower = (v: string): string => v.replace(/[A-Z]/g, c => c.toLowerCase());
const trim = (v: string): string => v.replace(/^[ \t\n\r\0\v]+|[ \t\n\r\0\v]+$/g, '');
const requireArray = (v: unknown): ItemFieldRow => { if (!arrayLike(v)) throw new TypeError('Feat source requires a PHP array'); return v; };
const stringArg = (v: unknown): string => { if (v == null || arrayLike(v)) throw new TypeError('Feat source key requires a scalar string'); return phpJsonString(v); };
export interface FeatEssenceSourceDependencies extends FormulaReadDependencies {
    metadata: FormulaVariableMetadata & {allEssences: readonly {variable_name: string; item_id: string}[]};
    queryFullItem: (id: string) => ItemFieldRow | null;
    resolveItemName: (id: string) => unknown;
}
/** Feat instance choices and direct feat bonuses, using actual definition and
 * provider reads. Repeatable source entries are retained rather than UUID-deduped.
 */
export function createFeatEssenceSourceCollector(deps: FeatEssenceSourceDependencies) {
    const readers = createFormulaReadContext(deps), emit = (e: readonly unknown[]): void => { deps.trace?.(e); };
    const query = (id: string): ItemFieldRow | null => { emit(['full-item', id]); return deps.queryFullItem(id); };
    const essenceIds = new Map(deps.metadata.allEssences.map(essence => [lower(essence.item_id), essence.item_id]));
    function essenceChoiceId(raw: unknown, held?: ItemFieldRow): string | null {
        const id = phpJsonString(raw ?? ''); if (id === '' || !valid(id)) return null;
        // Layer-less contract essences may be held as name-only shells, or absent.
        const constant = essenceIds.get(lower(id)); if (constant) return constant;
        const pick = held ?? query(id);
        if (readers.itemField(pick, 'item_type') !== 'essence') return null;
        const pickedId = phpJsonString(readers.itemField(pick, 'item_id'));
        return valid(pickedId) ? pickedId : null;
    }
    function entries(host: ItemFieldRow): ItemFieldRow {
        let result = readers.layerEntries(host, 'feat');
        const derived = path(host, 'computed', 'creature', 'skill_ability', 'feat');
        if (!arrayLike(derived) || !Object.keys(derived).length) return result;
        const seen = new Set(Object.values(result).map(readOverrideEntryAbilityId).filter(id => id !== ''));
        for (const entry of Object.values(derived)) {
            const id = readOverrideEntryAbilityId(entry); if (id !== '' && seen.has(id)) continue;
            if (id !== '') seen.add(id); result = appendPhpCompileArray(result, entry);
        }
        return result;
    }
    function definition(entry: unknown, host: ItemFieldRow): unknown {
        let value = entry;
        if (arrayLike(entry)) {
            value = own(entry, 'item_arr');
            const id = own(entry, 'item_id');
            if (!has(entry, 'item_arr') && typeof id === 'string' && valid(id)) value = id;
        }
        return layerEntryNeedsResolve(value) ? resolveLayerEntry(value, host, query) : value;
    }
    function inlineChoice(input: ItemFieldRow): string | null {
        const entry = requireArray(input);
        for (const key of ['exact_choice_item_id', 'choice_item_id']) {
            const id = essenceChoiceId(own(entry, key)); if (id) return id;
        }
        return null;
    }
    function userChoice(input: ItemFieldRow, rawEntry: ItemFieldRow): string | null {
        const host = requireArray(input), entry = requireArray(rawEntry), instance = lower(phpJsonString(own(entry, 'exact_choice_item_id') ?? ''));
        if (instance === '' || !valid(instance)) return null;
        const rows = own(host, 'user_choices');
        if (arrayLike(rows)) for (const row of Object.values(rows)) {
            if (!arrayLike(row) || !phpJsonString(own(row, 'choice_type') ?? '').endsWith('_essence_feat')) continue;
            if (lower(phpJsonString(own(row, 'exact_choice_item_id') ?? '')) !== instance) continue;
            const id = essenceChoiceId(own(row, 'choice')); if (id) return id;
        }
        const nested = path(host, '_user_choice_nested_map', instance);
        if (arrayLike(nested)) for (const row of Object.values(nested)) {
            if (!arrayLike(row) || !phpJsonString(own(row, 'choice_type') ?? '').endsWith('_essence_feat')) continue;
            const pick = own(row, 'item_arr');
            if (arrayLike(pick)) {
                const id = essenceChoiceId(readers.itemField(pick, 'item_id'), pick); if (id) return id;
            }
        }
        return null;
    }
    function choice(input: ItemFieldRow, rawId: string): ItemFieldRow {
        const host = requireArray(input), id = stringArg(rawId), lines: Row[] = []; let total = 0;
        if (id === '' || !valid(id)) return {rolled_total: 0, lines};
        for (const entry of Object.values(entries(host))) {
            if (!arrayLike(entry)) continue;
            const feat = definition(entry, host); if (!arrayLike(feat)) continue;
            let authored = false;
            for (const essence of deps.metadata.allEssences) {
                const key = 'feat_' + (essence.variable_name ?? '');
                if (key !== 'feat_' && Object.keys(readers.layerEntries(feat, key)).length) { authored = true; break; }
            }
            if (authored || Object.keys(readers.layerEntries(feat, 'essence_feat')).length) continue;
            if ((inlineChoice(entry) ?? userChoice(host, entry)) !== id) continue;
            total++; emit(['name', id]); const name = deps.resolveItemName(id);
            if (arrayLike(name)) throw new TypeError('Feat essence name requires a scalar string');
            const label = phpJsonString(name);
            lines.push({label: 'Feat essence bonus (' + (trim(label) === '' ? 'selection' : label) + ')', kind: 'feat_essence_bonus', value: 1, href_id: id});
        }
        return {rolled_total: total, lines};
    }
    function direct(input: ItemFieldRow, rawKey: string): ItemFieldRow {
        const host = requireArray(input), key = stringArg(rawKey), lines: Row[] = []; let total: PhpArithmetic = 0n;
        // The reader already bridges to the computed bucket, preserving each take.
        const feats = Object.values(entries(host));
        for (const entry of feats) {
            const feat = definition(entry, host); if (!arrayLike(feat)) continue;
            const scored = scoreCarrierIntegerLayerArithmetic(feat, key, 0, false, deps.metadata, readers), value = typeof scored === 'bigint' ? scored : phpFloatToInteger(scored);
            if (value === 0n) continue; total = phpAdd(total, value);
            const id = phpJsonString(readers.itemField(feat, 'item_id') ?? '');
            lines.push({label: phpJsonString(readers.itemField(feat, 'item_name') ?? 'Feat'), kind: 'feat', value: Number(value), href_id: valid(id) ? id : ''});
        }
        return {rolled_total: Number(total), lines};
    }
    return {choice, direct, inlineChoice, userChoice};
}
